;; Copyright (C) 2015-2018, Doctor Evidence. All rights reserved.

(ns dre.common.document-util
  "Utils for articles."
  {:authors ["Andrew Dolby" "Jozef Wagner"
             "Michiel Borkent" "Joel Kuiper"]}
  (:require #?(:cljs [dre.config :refer [config]])
            #?(:clj [clojure.tools.logging :refer [warn]]
               :cljs [taoensso.timbre :refer [warn]])
            [clojure.set :as cset]
            [clojure.string :as str]
            [dre.coll :refer [map-by nn-hash-map]]))

#?(:clj
   (defn categories
     [cfg]
     (get-in cfg [:dre :annotation :categories]))
   :cljs
   (defn categories
     [_cfg]
     (get-in (if (fn? config) (config) config) [:annotation :categories])))

(defn annotation-id
  [document-id annotation]
  (str document-id "-" (:subject-id annotation) "-"
       (str/join "-" (flatten (:selection annotation)))))

(defn ungroup-annotation
  [annotation]
  (map vector (:selection annotation) (repeat annotation)))

(defn annotation-positions
  [annotation]
  (let [mf (fn [[offset length]] (range offset (+ offset length)))]
    (mapcat mf (:selection annotation))))

(defn annotation-by-positions
  [annotation]
  (let [positions (annotation-positions annotation)]
    (map vector positions (repeat annotation))))

(def collect-spans
  (let [emf (fn [entry prev]
              (let [[pos] entry
                    [prev-pos] prev]
                (if (or (nil? prev-pos) (== 1 (- pos prev-pos)))
                  [entry]
                  [nil entry])))
        pf #(let [anns (second %)]
              (when (seq anns) anns))

        mf (fn [xs]
             (let [offset (first (first xs))
                   last (first (last xs))
                   anns (second (first xs))]
               (when offset
                 [[offset (inc (- last offset))] anns])))]
    (fn [entries]
      (let [entries (mapcat emf entries (cons nil entries))
            spans (partition-by pf entries)]
        (keep mf spans)))))

(defn pico-tags
  [cfg]
  (set (vals (categories cfg))))

(def mpico-tags
  (memoize pico-tags))

(defn pico->kw
  [cfg]
  (merge {"M" :misc}
         (cset/map-invert (categories cfg))))

(def mpico->kw
  (memoize pico->kw))

(defn subject-pico
  [cfg anns subject]
  (let [pico-tags (mpico-tags cfg)
        pico->kw (mpico->kw cfg)
        tags (or (concat
                  (filter pico-tags (:tags subject))
                  (filter pico-tags (mapcat :tags anns))))]
    (map pico->kw (if (empty? tags) ["M"] tags))))

(defn subject-ds-tags
  [anns subject]
  (let [tags (distinct (concat
                        (:ds-tags subject)
                        (mapcat :ds-tags anns)))]
    (if (empty? tags) [] tags)))

(defn subject-label
  [subject]
  (let [phrase (:phrase subject)]
    (str/join ", " (map :label phrase))))

(defn text-span-label
  [text span]
  (let [[offset length] span]
    (subs text offset (+ offset length))))

(defn text-selection-label
  [text selection]
  (str/join " " (map #(text-span-label text %) selection)))

(defn annotation-label
  [text annotation]
  (text-selection-label text (:selection annotation)))

(defn space-index
  "Returns index of space that occurs near offset index in text."
  [text index]
  (when text (str/index-of text " " index)))

(defn make-report
  [cfg annotations text subjects]
  (let [report (let [subject->annotations (group-by :subject-id annotations)

                     rf (fn rf-fn-anon [subject]
                          (let [anns (subject->annotations (:subject-id subject))]
                            ;; freq table does not show suggestions
                            (merge subject
                                   {:count (count anns)
                                    :label (subject-label subject)
                                    :unbound (mapv #(annotation-label text %) anns)
                                    :pico (subject-pico cfg anns subject)
                                    :ds-tags (subject-ds-tags anns subject)})))]
                 ;; freq report
                 (into []
                       (comp (map (fn [subject-id+subject]
                                    (val subject-id+subject)))
                             (keep rf))
                       subjects))]
    report))

(defn patent-link-id
  [id]
  (if (str/starts-with? id "US-")
    (let [coll (str/split id #"-")
          us-fix? (and (= 3 (count coll))
                       (= "US" (first coll))
                       (= 10 (count (second coll)))
                       (str/starts-with? id "US-20")
                       (= 2 (count (nth coll 2))))]
      (if us-fix?
        ;; recent US patent ids must be 4 numbers for year plus 7 numbers
        (str/join "" [(first coll) (apply str (take 4 (second coll)))
                                    "0" (apply str (drop 4 (second coll))) (nth coll 2)])
        (str/join "" coll)))
    (str/replace id "-" "")))

(defn process-response
  "Given the results as generated by the server, will attempt to make
  output usable by the front-end."
  [cfg {:keys [:annotation :report? :subjects :guid-map :no-guid? :no-paths?]}]
  (let [{:keys [:annotations :node/text]} annotation
        subjects (or subjects (:subjects annotation))
        guid-map (or guid-map (:guid-map annotation))
        _ (when-not (and subjects (or guid-map no-guid?))
            (warn "No subjects and/or guid-map provided"))

        ;; report returns all subjects, including suggestions
        report (when report? (make-report cfg annotations text subjects))


        ;; {idx ann, idxN ann, ...}
        position->annotations (map-by (mapcat annotation-by-positions annotations))

        ;; collect spans:
        ;; - merge sequential anns, might be more than one
        span->annotations (collect-spans (sort-by first position->annotations))

        hf (fn hf-fn-anon [[span anns]]
             (let [senses (into #{} (mapcat :senses) anns)
                   senses (into senses (mapcat :ambig-senses) anns)
                   senses (into senses (mapcat :alt-phrase-senses) anns)
                   ;; :senses and :paths-senses is not always overlapping, why not?

                   anns (concat (remove :is-suggestion anns)
                                (filter :is-suggestion anns))
                   subject-ids (keep :subject-id anns)

                   top-subject (get subjects
                                    (keyword
                                     (first subject-ids)))
                   uf (fn uf-fn-anon [phrase]
                        (mapv #(assoc % :guid
                                      (or (get guid-map
                                               (:uri %))
                                          (get guid-map
                                               (keyword (:uri %)))))
                              phrase))
                   top-subject (if no-guid?
                                 top-subject
                                 (update top-subject :phrase uf))
                   top-subject (if no-paths?
                                  (dissoc top-subject :paths :paths-senses)
                                  top-subject)
                   senses (into senses (:paths-senses top-subject))
                   highlight-id (str "ann-id-" (str/join "-" span))
                   [offset length] span
                   ftspan [offset (+ offset length)]]
               #_(when (seq (filter nil? senses))
                 (warn "NIL SENSE" top-subject))
               (nn-hash-map
                :id highlight-id
                :span span
                :ftspan ftspan
                :subject-id (when-not no-paths? (:subject-id top-subject))
                :top-subject top-subject
                :is-suggestion? (every? identity (map :is-suggestion anns))
                :pico (vec (subject-pico cfg anns top-subject))
                :ds-tags (vec (subject-ds-tags anns top-subject))
                :senses (vec (remove nil? senses)))))
        highlights (mapv hf span->annotations)
        highlights (sort-by :span highlights)]
    (nn-hash-map :text text
                 :report report
                 :guid-map (when-not no-guid? guid-map)
                 :highlights highlights)))

(defn article-title
  [result & {:keys [:tag]}]
  (let [spans (let [key (or tag :article/title)
                    sf (fn [{:keys [:tags]}] (some #(= key (keyword %)) tags))
                    spans (filter sf (:article/sentences result))]
                spans)
        texts (map :text spans)]
    (str/join ". " (map #(let [s (str/trim %)] (if (str/ends-with? s ".")
                                                 (str/trim (subs s 0 (dec (count s))))
                                                 s))
                        (remove str/blank? texts)))))

(defn span-map
  "TODO: docstring"
  ;; Annotate using spans
  [string spans offset to]
  (let [offset (or offset 0)
        rf (fn [mem val] (assoc mem (:ftspan val) val))
        ;; :ftspan = index-tuple like [18 20]
        ;; meta-map is a map from index-tuples to values
        meta-map (reduce rf {} spans)
        sorted-spans (sort-by #(first (:ftspan %)) spans)
        flat-spans
        (distinct (concat [0]
                          (flatten (map :ftspan sorted-spans))
                          [(+ (count string) offset)]))
        spanf (fn [curr]
                {:meta (or (get meta-map (into [] curr))
                           {:plain? true
                            :ftspan [(+ offset (max 0 (- (first curr) offset)))
                                     (min to (second curr))]})
                 :str (subs
                       string
                       (max 0 (- (first curr) offset))
                       (- (min to (second curr)) offset))})]
    (loop [s (partition 2 1 flat-spans)
           v (transient [])]
      (if (empty? s)
        (persistent! v)
        (let [curr (first s)
              new-v (if (and (< (first curr) (second curr))
                             (< (first curr) to)
                             (<= 0 (- (second curr) offset)))
                      (conj! v (spanf curr))
                      v)]
          (recur (rest s) new-v))))))

(defn author-short
  [author]
  (let [{:keys [:author/name]} author
        {:keys [:author/given-names :author/surnames]} name]
    (str (first surnames) " "
         (apply str (map first (concat given-names (rest surnames)))))))

(defn authors-short
  [authors]
  (if-not (empty? authors)
    (let [et-al? (< 3 (count authors))
          authors (take 3 authors)]
      (str (str/join ", " (map author-short authors))
           (when et-al? ", et al.")))
    "Unknown"))

(defn invert-groups
  [groups]
  (dre.coll/flat-group-by
   key
   (cset/map-invert groups)))

(defn invert-groups-by-keys
  [groups ks]
  (-> (invert-groups groups)
      (map-by
       #(select-keys (first %) ks)
       second)))

(defn article-id->group-id
  ([groups guid]
   (-> (invert-groups-by-keys groups [:article/guid])
       (get {:article/guid guid})
       ((comp second first first))))
  ([groups source uri]
   (-> (invert-groups-by-keys groups [:article/uri :article/source])
       (get {:article/source source :article/uri uri})
       ((comp second first first)))))


(defn guid->group-id
  [guid groups]
  (article-id->group-id groups guid))

;;;; Scratch

(comment

 ,)
